import Tabs from '@mui/material/Tabs'
import Tab from '@mui/material/Tab'
import Box from '@mui/material/Box'
import { SyntheticEvent, useEffect, useState } from 'react'
import { Link, useLocation, matchPath } from 'react-router-dom'
import { a11yProps } from './utils'
import { NavTabsProps, NavTabsVariant } from './tabs.types'
import { styled, SxProps, Theme } from '@mui/material/styles'
import { SPACING } from '@common/theme/spacing'

export default function EnhancedNavTabs({ routes, variant }: NavTabsProps) {
  const location = useLocation()

  const getMatchingTabIndex = () => {
    const currentPath = location.pathname

    return routes.findIndex((route) => {
      if (route.path.includes('#')) {
        return route.path === location.hash || route.path === `${location.pathname}${location.hash}`
      }

      try {
        if (route.path === currentPath) {
          return true
        }

        const isMainMatch = matchPath(route.path, currentPath) !== null
        if (isMainMatch) {
          return true
        }

        const aliasRoutes = route.aliasRoutes
        if (aliasRoutes && aliasRoutes.length > 0) {
          return aliasRoutes.some((aliasPath) => {
            return matchPath(aliasPath, currentPath) !== null
          })
        }

        return false
      } catch (e) {
        console.warn('Error matching route:', e)
        return false
      }
    })
  }

  const initialIndex = getMatchingTabIndex()
  const [value, setValue] = useState(initialIndex !== -1 ? initialIndex : 0)

  const StyledTabs = styled(Tabs)(({ theme }) => ({
    backgroundColor: theme.palette.secondary.light,
    borderRadius: `${theme.spacing(SPACING.borderRadiusSm)} !important`,
    padding: theme.spacing(SPACING.tabsSpacing),
    minHeight: 'auto',
  }))

  const mapVariantToSxProp = (index: number): SxProps<Theme> => ({
    borderRadius: (theme) => theme.spacing(SPACING.borderRadiusSm),
    ...(variant === NavTabsVariant.SEGMENTED_CONTROLS
      ? {
          color: (theme) => `${theme.palette.text.primary} !important`,
          typography: 'subtitle1',
          backgroundColor: index === value ? 'background.default' : 'transparent',
          paddingY: (theme) => `${theme.spacing(SPACING.noSpacing)} !important`,
          minHeight: (theme) => `${theme.spacing(4.5)} !important`,
          '&.Mui-selected': {
            color: (theme) => `${theme.palette.primary.main} !important`,
          },
        }
      : {
          color: (theme) => `${theme.palette.text.secondary} !important`,
          typography: 'button',
          '&.Mui-selected': {
            color: (theme) => `${theme.palette.primary.main} !important`,
          },
        }),
  })

  useEffect(() => {
    const currentIndex = getMatchingTabIndex()
    if (currentIndex !== -1) {
      setValue(currentIndex)
    }
  }, [location.pathname, location.hash])

  useEffect(() => {
    const currentIndex = getMatchingTabIndex()
    if (currentIndex !== -1 && currentIndex !== value) {
      setValue(currentIndex)
    }
  }, [routes])

  const handleChange = (_event: SyntheticEvent, newValue: number) => {
    setValue(newValue)
  }

  return (
    <Box sx={{ display: 'flex' }}>
      <StyledTabs
        value={value}
        onChange={handleChange}
        aria-label="enhanced tabs"
        data-testid="tabs"
        sx={{ paddingBottom: 0 }}
        TabIndicatorProps={{
          style: variant === NavTabsVariant.SEGMENTED_CONTROLS ? { display: 'none' } : {},
        }}
      >
        {routes.map((route, index) =>
          route.paginationDetails ? (
            <Tab
              key={route.path}
              data-testid={`tab-${index}`}
              component={Link}
              to={{ pathname: route.path, search: `?${route.paginationDetails}` }}
              sx={mapVariantToSxProp(index)}
              label={route.label}
              {...a11yProps(index)}
            />
          ) : (
            <Tab
              key={route.path}
              data-testid={`tab-${index}`}
              component={Link}
              to={route.path}
              sx={mapVariantToSxProp(index)}
              label={route.label}
              {...a11yProps(index)}
            />
          ),
        )}
      </StyledTabs>
    </Box>
  )
}
